/**
 * Version control on web (plan 7.5): the compact control (Previous version,
 * "Version 3 of 5", Next version) whose label opens the version list, and
 * the banner of an older version (Restore this version, Back to latest).
 * The arrows mirror in RTL. `VersionControl.tsx` is the native twin.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { SpinnerGlyph } from '../../common/SpinnerGlyph';
import { WebMenu } from '../../web/WebMenu';
import { relativeVersionTime } from './version-model';
import type { VersionControl as VersionControlState } from './useVersionControl';

export function VersionControl({ control, compact = false }: { control: VersionControlState; compact?: boolean }) {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const labelRef = useRef<HTMLButtonElement | null>(null);
  const { model } = control;
  if (!model.visible || !model.current) return null;
  const label = t('chat.artifacts.versions.label', { number: model.current.number, total: model.total });
  const locale = i18n.language || 'en';

  return (
    <div
      className={`tabbio-version-control${compact ? ' is-compact' : ''}`}
      role="group"
      aria-label={t('chat.artifacts.versions.history')}
      onKeyDown={(event) => {
        // Escape closes the list, not the panel around it.
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
          labelRef.current?.focus();
        }
      }}
    >
      <button
        type="button"
        className="tabbio-version-step"
        onClick={() => control.goTo(model.previous, 'previous')}
        disabled={!model.previous}
        aria-label={t('chat.artifacts.versions.previous')}
        title={t('chat.artifacts.versions.previous')}
      >
        <MaterialSymbol name="chevron_left" size={20} className="tabbio-mirror-rtl" />
      </button>
      <button
        ref={labelRef}
        type="button"
        className="tabbio-version-label"
        onClick={() => {
          setOpen((value) => !value);
          control.refetchRows();
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t('chat.artifacts.versions.history')}
      >
        {label}
      </button>
      <button
        type="button"
        className="tabbio-version-step"
        onClick={() => control.goTo(model.next, 'next')}
        disabled={!model.next}
        aria-label={t('chat.artifacts.versions.next')}
        title={t('chat.artifacts.versions.next')}
      >
        <MaterialSymbol name="chevron_right" size={20} className="tabbio-mirror-rtl" />
      </button>
      <WebMenu
        open={open}
        onClose={() => setOpen(false)}
        anchor={labelRef.current}
        placement="bottom-start"
        minWidth={300}
        mobileSheet
        style={{ borderRadius: 16, maxHeight: 'min(70vh, 520px)', overflowY: 'auto' }}
      >
        <div className="tabbio-version-list-title">{t('chat.artifacts.versions.history')}</div>
        {control.loadingRows ? (
          <div className="tabbio-version-list-note" role="status">
            <SpinnerGlyph color="var(--text-secondary)" size={16} />
            {t('chat.artifacts.versions.loading')}
          </div>
        ) : control.rowsFailed ? (
          <div className="tabbio-version-list-note">{t('chat.artifacts.versions.loadFailed')}</div>
        ) : null}
        {control.rows.map((row) => {
          const byline = t(row.byline.key, row.byline.params ?? {});
          const time = relativeVersionTime(row.createdAt, locale);
          const selectable = row.status === 'ready';
          return (
            <button
              key={row.id}
              type="button"
              role="menuitemradio"
              aria-checked={row.selected}
              disabled={!selectable}
              className={`tabbio-version-row${row.selected ? ' is-selected' : ''}`}
              onClick={() => {
                setOpen(false);
                control.goTo({ id: row.id, number: row.number }, 'list');
              }}
            >
              <span className="tabbio-version-row-head">
                <span className="tabbio-version-row-number">
                  {t('chat.artifacts.versions.row', { number: row.number })}
                </span>
                {row.isCurrent ? (
                  <span className="tabbio-version-row-tag">{t('chat.artifacts.versions.latest')}</span>
                ) : null}
                {row.live ? (
                  <span className="tabbio-version-row-live" title={t('chat.artifacts.versions.live')}>
                    <MaterialSymbol name="public" size={16} />
                    <span className="tabbio-visually-hidden">{t('chat.artifacts.versions.live')}</span>
                  </span>
                ) : null}
                {row.selected ? <MaterialSymbol name="check" size={18} className="tabbio-version-row-check" /> : null}
              </span>
              <span className="tabbio-version-row-meta">
                {[time, byline].filter(Boolean).join(' · ')}
                {row.status === 'pending' ? ` · ${t('chat.artifacts.versions.pending')}` : ''}
                {row.status === 'failed' ? ` · ${t('chat.artifacts.versions.failed')}` : ''}
              </span>
              {row.summary || row.error ? (
                <span className="tabbio-version-row-summary" dir="auto">
                  {row.error ?? row.summary}
                </span>
              ) : null}
            </button>
          );
        })}
      </WebMenu>
    </div>
  );
}

/** "You're viewing version 2." with Restore this version and Back to latest. */
export function OlderVersionBanner({
  control,
  onRestored,
  onRestoreFailed,
}: {
  control: VersionControlState;
  onRestored: (number: number) => void;
  onRestoreFailed: () => void;
}) {
  const { t } = useTranslation('common');
  const current = control.model.current;
  if (control.model.isLatest || !current) return null;
  return (
    <div className="tabbio-version-banner" role="status">
      <MaterialSymbol name="history" size={20} />
      <span className="tabbio-version-banner-text">
        {t('chat.artifacts.versions.viewingOld', { number: current.number })}
      </span>
      <span className="tabbio-version-banner-actions">
        <button type="button" className="tabbio-text-button" onClick={control.backToLatest}>
          {t('chat.artifacts.versions.backToLatest')}
        </button>
        <button
          type="button"
          className="tabbio-panel-primary is-small"
          disabled={control.restoring}
          aria-busy={control.restoring || undefined}
          onClick={() => {
            void control.restore().then((number) => {
              if (number) onRestored(number);
              else onRestoreFailed();
            });
          }}
        >
          {control.restoring ? t('chat.artifacts.versions.restoring') : t('chat.artifacts.versions.restore')}
        </button>
      </span>
    </div>
  );
}
